// Login / sign up, in a window over the drawn table (as in the creator's mockup).
// Email is optional (sign up only). Discord / Google buttons show only when
// the server has them enabled.
import { useEffect, useState, type FormEvent } from "react";
import { EMAIL_MAX, EmailSchema, PASSWORD_MAX, USERNAME_MAX, type Me, type OAuthProvider } from "@wp/shared";
import { api, loginProblem, oauthStartPath, registerProblem } from "../../net/api";
import { RoomBackdrop } from "../common/RoomBackdrop";

interface Props {
  onAuthed: (me: Me) => void;
  initialError: string | null;
}

const PROVIDER_LABEL: Record<OAuthProvider, string> = { discord: "Continue with Discord", google: "Continue with Google" };

export function AuthScreen({ onAuthed, initialError }: Props) {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string | null>(initialError);
  const [busy, setBusy] = useState(false);
  const [providers, setProviders] = useState<OAuthProvider[]>([]);

  useEffect(() => {
    let alive = true;
    void api.providers().then((res) => {
      if (!alive || !res.ok) return;
      setProviders((["discord", "google"] as const).filter((p) => res.value[p]));
    });
    return () => {
      alive = false;
    };
  }, []);

  async function submit(mode: "login" | "register") {
    if (busy) return;
    let problem = mode === "login" ? loginProblem(username, password) : registerProblem(username, password);
    if (!problem && mode === "register" && email !== "" && !EmailSchema.safeParse(email).success) {
      problem = "That email does not look right (or leave it empty).";
    }
    if (problem) {
      setError(problem);
      return;
    }
    setBusy(true);
    setError(null);
    const res =
      mode === "login" ? await api.login(username, password) : await api.register(username, password, email || undefined);
    setBusy(false);
    if (res.ok) onAuthed(res.value);
    else setError(res.error.message);
  }

  function onSubmit(e: FormEvent) {
    e.preventDefault();
    void submit("login");
  }

  return (
    <main className="auth-screen">
      <RoomBackdrop />
      <div className="auth-window window">
        <h1 className="logo">Wizard Poker</h1>
        <p className="logo-sub" aria-hidden="true">// back room · table server 01 online</p>
        <form onSubmit={onSubmit} className="auth-form" noValidate>
          <label className="field big">
            <span>Username:</span>
            <input
              data-testid="auth-username"
              autoComplete="username"
              placeholder="Enter Username here"
              value={username}
              maxLength={USERNAME_MAX}
              onChange={(e) => setUsername(e.target.value)}
              autoCapitalize="off"
              spellCheck={false}
            />
          </label>
          <label className="field big">
            <span>Password:</span>
            <input
              data-testid="auth-password"
              type="password"
              autoComplete="current-password"
              placeholder="Enter Password here"
              value={password}
              maxLength={PASSWORD_MAX}
              onChange={(e) => setPassword(e.target.value)}
            />
          </label>
          <label className="field">
            <span>Email (optional, only when signing up):</span>
            <input
              data-testid="auth-email"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              maxLength={EMAIL_MAX}
              onChange={(e) => setEmail(e.target.value)}
            />
          </label>
          {error && (
            <p className="form-error" role="alert" data-testid="auth-error">
              {error}
            </p>
          )}
          <div className="auth-actions">
            <button
              type="button"
              className="btn big"
              disabled={busy}
              onClick={() => void submit("register")}
              data-testid="auth-register"
            >
              Sign Up
            </button>
            <button type="submit" className="btn big primary" disabled={busy} data-testid="auth-login">
              Log In
            </button>
          </div>
        </form>
        {providers.length > 0 && (
          <div className="oauth">
            {providers.map((p) => (
              <button
                key={p}
                type="button"
                className={`btn oauth-btn ${p}`}
                onClick={() => window.location.assign(oauthStartPath(p))}
                data-testid={`auth-${p}`}
              >
                {PROVIDER_LABEL[p]}
              </button>
            ))}
          </div>
        )}
        <p className="age-label" data-testid="age-label">
          <strong>13+</strong> Players are expected to be 13 or older to play.
        </p>
        <p className="legal-links small">
          By playing you agree to the <a href="/terms">Terms of Service</a> and <a href="/privacy">Privacy Policy</a>.
        </p>
      </div>
    </main>
  );
}
